import React, {useState} from "react";
import {
    Box, Typography, Paper, CircularProgress, Divider, Alert,
    Table, TableBody, TableCell, TableContainer, TableRow,
} from "@mui/material";
import {accountingApi} from "../../services/api/accounting";
import {showSnackbar} from "../../components/SnackBar/SnackBar";
import ReportFilters from "./ReportFilters";

const formatMoney = (amount) => {
    if (amount === null || amount === undefined) return "KES 0.00";
    const num = Number(amount);
    const formatted = Math.abs(num).toLocaleString("en-KE", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    return num < 0 ? `(KES ${formatted})` : `KES ${formatted}`;
};

export default function ProfitAndLoss() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const handleApply = async (params) => {
        setLoading(true);
        try {
            const response = await accountingApi.getProfitAndLoss(params);
            if (response.ok && response.data) {
                setData(response.data);
            } else {
                setData(null);
            }
        } catch (err) {
            console.error("Failed to fetch P&L:", err);
        } finally {
            setLoading(false);
        }
    };

    const handlePDF = async (params) => {
        setDownloading(true);
        try {
            const response = await accountingApi.downloadProfitAndLossPDF(params);
            if (response.ok && response.blob) {
                const url = window.URL.createObjectURL(response.blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Profit_And_Loss_${params.start_date}_to_${params.end_date}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            } else {
                showSnackbar({title: "Error!", message: "Failed to download PDF.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
            }
        } catch (err) {
            showSnackbar({title: "Error!", message: "Failed to download PDF.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{py: 3}}>
            <Typography variant="h5" fontWeight={600} sx={{mb: 3, color: "#2C5283"}}>
                Profit & Loss Statement
            </Typography>
            <ReportFilters onApply={handleApply} onDownloadPDF={handlePDF} downloading={downloading}/>

            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 6}}><CircularProgress/></Box>
            ) : !data ? (
                <Paper sx={{py: 6, textAlign: "center", borderRadius: 2}}>
                    <Typography color="textSecondary">Select a date range and click Apply to generate the report.</Typography>
                </Paper>
            ) : (
                <Paper sx={{borderRadius: 2, overflow: "hidden"}}>
                    {data.period && (
                        <Box sx={{px: 3, py: 1.5, bgcolor: "#f5f7fa", borderBottom: "1px solid #e0e0e0"}}>
                            <Typography variant="body2" color="textSecondary">
                                Period: {data.period.start} to {data.period.end}
                            </Typography>
                        </Box>
                    )}

                    {/* Revenue */}
                    <Box sx={{px: 3, py: 2}}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{color: "#2e7d32", mb: 1}}>Revenue</Typography>
                        {data.revenue?.length > 0 ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableBody>
                                        {data.revenue.map((item, i) => (
                                            <TableRow key={i}>
                                                <TableCell sx={{border: "none", pl: 2}}>{item.code} - {item.name}</TableCell>
                                                <TableCell align="right" sx={{border: "none", fontWeight: 500}}>{formatMoney(item.net_amount)}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell sx={{fontWeight: 700, borderTop: "2px solid #2e7d32", color: "#2e7d32"}}>Total Revenue</TableCell>
                                            <TableCell align="right" sx={{fontWeight: 700, borderTop: "2px solid #2e7d32", color: "#2e7d32"}}>{formatMoney(data.total_revenue)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : <Typography variant="body2" color="textSecondary" sx={{pl: 2}}>No revenue in this period.</Typography>}
                    </Box>

                    <Divider/>

                    {/* Expenses */}
                    <Box sx={{px: 3, py: 2}}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{color: "#e65100", mb: 1}}>Expenses</Typography>
                        {data.expenses?.length > 0 ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableBody>
                                        {data.expenses.map((item, i) => (
                                            <TableRow key={i}>
                                                <TableCell sx={{border: "none", pl: 2}}>{item.code} - {item.name}</TableCell>
                                                <TableCell align="right" sx={{border: "none", fontWeight: 500}}>{formatMoney(item.net_amount)}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell sx={{fontWeight: 700, borderTop: "2px solid #e65100", color: "#e65100"}}>Total Expenses</TableCell>
                                            <TableCell align="right" sx={{fontWeight: 700, borderTop: "2px solid #e65100", color: "#e65100"}}>{formatMoney(data.total_expenses)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : <Typography variant="body2" color="textSecondary" sx={{pl: 2}}>No expenses in this period.</Typography>}
                    </Box>

                    <Divider/>

                    {/* Net Income & Tax */}
                    <Box sx={{px: 3, py: 2, bgcolor: data.net_income >= 0 ? "#e8f5e9" : "#ffebee"}}>
                        <Box sx={{display: "flex", justifyContent: "space-between", mb: 1}}>
                            <Typography variant="h6" fontWeight={700}>Net Income</Typography>
                            <Typography variant="h6" fontWeight={700} sx={{color: data.net_income >= 0 ? "#2e7d32" : "#c62828"}}>
                                {formatMoney(data.net_income)}
                            </Typography>
                        </Box>
                        <Divider sx={{my: 1}}/>
                        <Box sx={{display: "flex", justifyContent: "space-between", mb: 0.5}}>
                            <Typography variant="body2" color="textSecondary">Tax Rate</Typography>
                            <Typography variant="body2">{data.tax_rate}%</Typography>
                        </Box>
                        <Box sx={{display: "flex", justifyContent: "space-between", mb: 0.5}}>
                            <Typography variant="body2" color="textSecondary">Estimated Tax</Typography>
                            <Typography variant="body2" sx={{color: "#c62828"}}>{formatMoney(data.estimated_tax)}</Typography>
                        </Box>
                        <Box sx={{display: "flex", justifyContent: "space-between"}}>
                            <Typography variant="subtitle1" fontWeight={600}>Net Income After Tax</Typography>
                            <Typography variant="subtitle1" fontWeight={600} sx={{color: data.net_income_after_tax >= 0 ? "#2e7d32" : "#c62828"}}>
                                {formatMoney(data.net_income_after_tax)}
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            )}
        </Box>
    );
}
